// src/components/UserAvatarDropdown.tsx

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  User,
  Settings,
  Video,
  BarChart2,
  Bell,
  LogOut,
  History,
  Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../lib/auth';
import { useCurrentUser, formatMemberSince } from '../contexts/UserContext';

// Theme-aware dropdown menu item
function DropdownMenuItem({
  onClick,
  icon,
  label,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center w-full px-3 py-2 rounded transition-all duration-200"
      style={{ color: 'var(--vb-text-primary)' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--vb-bg-hover)';
        e.currentTarget.style.color = 'var(--vb-accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'var(--vb-text-primary)';
      }}
    >
      {icon} {label}
    </button>
  );
}

export default function UserAvatarDropdown() {
  const { t } = useTranslation('home');
  const { currentUser, loading } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSignOut = async () => {
    await logoutUser();
    navigate('/');
  };

  // Don't render if not logged in or still loading
  if (loading || !currentUser) return null;

  const memberSince = formatMemberSince(currentUser.createdAt);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center space-x-2 focus:outline-none group"
      >
        <img
          src={currentUser.photoURL}
          alt="Avatar"
          className="w-10 h-10 rounded-full object-cover ring-2 ring-yellow-400 group-hover:ring-4 group-hover:ring-yellow-300 transition-all duration-200"
        />
        <ChevronDown
          className={`w-5 h-5 transition-transform duration-200 group-hover:text-yellow-400 ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--vb-text-primary)' }}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 backdrop-blur-lg rounded-lg shadow-xl z-50 overflow-hidden animate-fade-in"
          style={{
            backgroundColor: 'var(--vb-bg-secondary)',
            color: 'var(--vb-text-primary)',
          }}
        >
          {/* Profile Overview */}
          <div
            className="p-4"
            style={{ borderBottom: '1px solid var(--vb-border)' }}
          >
            <div className="flex items-center space-x-3">
              <img
                src={currentUser.photoURL}
                alt="Avatar"
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold">{currentUser.displayName}</p>
                <p className="text-sm" style={{ color: 'var(--vb-text-secondary)' }}>{memberSince}</p>
              </div>
            </div>
          </div>

          <ul className="p-2 space-y-1">
            <li>
              <DropdownMenuItem
                onClick={() => { navigate(`/profile/${currentUser.uid}`); setOpen(false); }}
                icon={<User className="w-5 h-5 mr-2" />}
                label={t('userMenu.myProfile')}
              />
            </li>

            <li>
              <DropdownMenuItem
                onClick={() => { navigate('/studio'); setOpen(false); }}
                icon={<Video className="w-5 h-5 mr-2" />}
                label={t('userMenu.myVideos')}
              />
            </li>

            <li>
              <DropdownMenuItem
                onClick={() => { navigate('/history/watch'); setOpen(false); }}
                icon={<History className="w-5 h-5 mr-2" />}
                label={t('userMenu.watchHistory')}
              />
            </li>

            <li>
              <DropdownMenuItem
                onClick={() => { navigate('/history/search'); setOpen(false); }}
                icon={<Search className="w-5 h-5 mr-2" />}
                label={t('userMenu.searchHistory')}
              />
            </li>

            <li>
              <DropdownMenuItem
                onClick={() => { navigate('/analytics'); setOpen(false); }}
                icon={<BarChart2 className="w-5 h-5 mr-2" />}
                label={t('userMenu.analytics')}
              />
            </li>

            <li>
              <DropdownMenuItem
                onClick={() => { navigate('/notifications'); setOpen(false); }}
                icon={<Bell className="w-5 h-5 mr-2" />}
                label={t('userMenu.notifications')}
              />
            </li>

            <li>
              <DropdownMenuItem
                onClick={() => { navigate('/settings'); setOpen(false); }}
                icon={<Settings className="w-5 h-5 mr-2" />}
                label={t('userMenu.settings')}
              />
            </li>
          </ul>

          <div style={{ borderTop: '1px solid var(--vb-border)' }}>
            <button
              onClick={handleSignOut}
              className="flex items-center w-full px-3 py-2 rounded hover:bg-red-600/20 transition text-red-500"
            >
              <LogOut className="w-5 h-5 mr-2" /> {t('userMenu.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
